/**
 * Excel (.xlsx) export, made in the browser with no library. A workbook is a zip
 * of a few XML files; this writes them stored (uncompressed), which Excel,
 * LibreOffice and Google Sheets all open.
 *  - the header row is bold, stays in view while scrolling, and has filters;
 *  - a Date, or an ISO date-time string, becomes an Excel date-time shown in
 *    India time (dd/mm/yyyy hh:mm:ss) — sortable and filterable as a date;
 *  - a number stays a number; anything else is text exactly as it is, so a
 *    mobile number, a MAC or a long ID never turns into 9.98E+09;
 *  - each column is as wide as its longest value (within reason).
 *
 * columns: [{ label, field }] | [{ label, value: (row) => … }] | ['field', …] —
 * the shape exportRowsToCsv takes, so a page's column list carries over. Any
 * `render` is ignored: export the plain value.
 */

const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
const IST_OFFSET_MS = 5.5 * 60 * 60 * 1000;
const ISO_DATE_TIME = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d+)?)?(Z|[+-]\d{2}:?\d{2})?$/;
const MAX_CELL_TEXT = 32767; // Excel's limit for one cell

const STYLE = { text: 0, header: 1, date: 2 };

const normalizeColumns = (columns) => columns.map((c) => (typeof c === 'string' ? { label: c, field: c } : c));

const cellValue = (column, row) => {
    if (typeof column.value === 'function') return column.value(row);
    return row?.[column.field];
};

/** UTF-8 bytes of a string (TextEncoder where there is one). */
function utf8(text) {
    if (typeof TextEncoder !== 'undefined') return new TextEncoder().encode(text);
    const binary = unescape(encodeURIComponent(text));
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    return bytes;
}

// Characters XML 1.0 cannot carry are dropped; the rest escaped.
const xml = (value) => String(value)
    // eslint-disable-next-line no-control-regex
    .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F￾￿]/g, '')
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

/** 0 → A, 25 → Z, 26 → AA. */
function columnLetter(index) {
    let n = index + 1;
    let letters = '';
    while (n > 0) {
        const rem = (n - 1) % 26;
        letters = String.fromCharCode(65 + rem) + letters;
        n = Math.floor((n - 1) / 26);
    }
    return letters;
}

/** A Date as Excel's serial number, in India time (Excel keeps no time zone). */
const excelSerial = (date) => (date.getTime() + IST_OFFSET_MS) / 86400000 + 25569;

/** What goes in one cell: { kind: 'date' | 'number' | 'text', value, width }. */
function cellOf(value, type) {
    if (value === null || value === undefined || value === '') return null;
    if (type !== 'text') {
        const date = value instanceof Date ? value
            : (typeof value === 'string' && ISO_DATE_TIME.test(value) ? new Date(value) : null);
        if (date && !Number.isNaN(date.getTime())) return { kind: 'date', value: excelSerial(date), width: 19 };
        if (typeof value === 'number' && Number.isFinite(value)) return { kind: 'number', value, width: String(value).length };
    }
    let text = typeof value === 'boolean' ? (value ? 'Yes' : 'No') : String(value);
    if (text.length > MAX_CELL_TEXT) text = text.slice(0, MAX_CELL_TEXT);
    return { kind: 'text', value: text, width: Math.max(...text.split('\n').map((line) => line.length)) };
}

function cellXml(ref, cell, style) {
    if (cell.kind === 'date') return `<c r="${ref}" s="${STYLE.date}"><v>${cell.value}</v></c>`;
    if (cell.kind === 'number') return `<c r="${ref}"${style ? ` s="${style}"` : ''}><v>${cell.value}</v></c>`;
    return `<c r="${ref}" t="inlineStr"${style ? ` s="${style}"` : ''}><is><t xml:space="preserve">${xml(cell.value)}</t></is></c>`;
}

// Sheet names: at most 31 characters, none of : \ / ? * [ ], unique in the book.
function sheetNames(sheets) {
    const used = new Set();
    return sheets.map((sheet, i) => {
        const base = String(sheet.name || `Sheet${i + 1}`).replace(/[:\\/?*[\]]/g, ' ').trim().slice(0, 31) || `Sheet${i + 1}`;
        let name = base;
        for (let n = 2; used.has(name.toLowerCase()); n++) name = `${base.slice(0, 31 - String(n).length - 1)} ${n}`;
        used.add(name.toLowerCase());
        return name;
    });
}

// The numbered column a sheet starts with when it asks for one (serial: true).
const SERIAL_COLUMN = { label: 'Sr. No.', serial: true };

function worksheetXml(columns, rows, { serial = false } = {}) {
    const cols = [...(serial ? [SERIAL_COLUMN] : []), ...normalizeColumns(columns)];
    const widths = cols.map((c) => String(c.label ?? '').length);
    const lines = [];

    const header = cols.map((c, i) => cellXml(`${columnLetter(i)}1`, { kind: 'text', value: String(c.label ?? '') }, STYLE.header));
    lines.push(`<row r="1">${header.join('')}</row>`);

    rows.forEach((row, r) => {
        const cells = [];
        cols.forEach((column, i) => {
            const cell = cellOf(column.serial ? r + 1 : cellValue(column, row), column.type);
            if (!cell) return;
            widths[i] = Math.max(widths[i], cell.width);
            cells.push(cellXml(`${columnLetter(i)}${r + 2}`, cell));
        });
        lines.push(`<row r="${r + 2}">${cells.join('')}</row>`);
    });

    const lastColumn = columnLetter(Math.max(cols.length, 1) - 1);
    const colsXml = cols.map((_, i) => `<col min="${i + 1}" max="${i + 1}" width="${Math.min(60, Math.max(8, widths[i] + 2))}" customWidth="1"/>`).join('');
    return {
        filterRef: `$A$1:$${lastColumn}$${rows.length + 1}`,
        xml: '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>'
            + '<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">'
            + '<sheetViews><sheetView workbookViewId="0"><pane ySplit="1" topLeftCell="A2" activePane="bottomLeft" state="frozen"/></sheetView></sheetViews>'
            + (colsXml ? `<cols>${colsXml}</cols>` : '')
            + `<sheetData>${lines.join('')}</sheetData>`
            + (cols.length ? `<autoFilter ref="A1:${lastColumn}${rows.length + 1}"/>` : '')
            + '</worksheet>',
    };
}

const STYLES_XML = '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>'
    + '<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">'
    + '<numFmts count="1"><numFmt numFmtId="164" formatCode="dd/mm/yyyy hh:mm:ss"/></numFmts>'
    + '<fonts count="2"><font><sz val="11"/><name val="Calibri"/></font><font><b/><sz val="11"/><name val="Calibri"/></font></fonts>'
    + '<fills count="2"><fill><patternFill patternType="none"/></fill><fill><patternFill patternType="gray125"/></fill></fills>'
    + '<borders count="1"><border><left/><right/><top/><bottom/><diagonal/></border></borders>'
    + '<cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>'
    + '<cellXfs count="3">'
    + '<xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/>'
    + '<xf numFmtId="0" fontId="1" fillId="0" borderId="0" xfId="0" applyFont="1"/>'
    + '<xf numFmtId="164" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1"/>'
    + '</cellXfs>'
    + '<cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles>'
    + '</styleSheet>';

// ── Zip (stored) ─────────────────────────────────────────────────────────────

const CRC_TABLE = (() => {
    const table = new Uint32Array(256);
    for (let n = 0; n < 256; n++) {
        let c = n;
        for (let k = 0; k < 8; k++) c = c & 1 ? 0xEDB88320 ^ (c >>> 1) : c >>> 1;
        table[n] = c >>> 0;
    }
    return table;
})();

function crc32(bytes) {
    let c = 0xFFFFFFFF;
    for (let i = 0; i < bytes.length; i++) c = CRC_TABLE[(c ^ bytes[i]) & 0xFF] ^ (c >>> 8);
    return (c ^ 0xFFFFFFFF) >>> 0;
}

function zipStored(files) {
    const parts = [];
    const central = [];
    let offset = 0;
    const DOS_DATE = 0x21; // 1980-01-01, 00:00
    for (const file of files) {
        const name = utf8(file.name);
        const data = file.data;
        const crc = crc32(data);

        const local = new DataView(new ArrayBuffer(30));
        local.setUint32(0, 0x04034b50, true);
        local.setUint16(4, 20, true);
        local.setUint16(6, 0x0800, true); // names in UTF-8
        local.setUint16(8, 0, true);      // stored
        local.setUint16(10, 0, true);
        local.setUint16(12, DOS_DATE, true);
        local.setUint32(14, crc, true);
        local.setUint32(18, data.length, true);
        local.setUint32(22, data.length, true);
        local.setUint16(26, name.length, true);
        local.setUint16(28, 0, true);
        parts.push(new Uint8Array(local.buffer), name, data);

        const entry = new DataView(new ArrayBuffer(46));
        entry.setUint32(0, 0x02014b50, true);
        entry.setUint16(4, 20, true);
        entry.setUint16(6, 20, true);
        entry.setUint16(8, 0x0800, true);
        entry.setUint16(10, 0, true);
        entry.setUint16(12, 0, true);
        entry.setUint16(14, DOS_DATE, true);
        entry.setUint32(16, crc, true);
        entry.setUint32(20, data.length, true);
        entry.setUint32(24, data.length, true);
        entry.setUint16(28, name.length, true);
        entry.setUint32(42, offset, true);
        central.push(new Uint8Array(entry.buffer), name);

        offset += 30 + name.length + data.length;
    }
    const centralSize = central.reduce((n, part) => n + part.length, 0);
    const end = new DataView(new ArrayBuffer(22));
    end.setUint32(0, 0x06054b50, true);
    end.setUint16(8, files.length, true);
    end.setUint16(10, files.length, true);
    end.setUint32(12, centralSize, true);
    end.setUint32(16, offset, true);

    const all = [...parts, ...central, new Uint8Array(end.buffer)];
    const out = new Uint8Array(all.reduce((n, part) => n + part.length, 0));
    let at = 0;
    for (const part of all) { out.set(part, at); at += part.length; }
    return out;
}

/**
 * The workbook's bytes. sheets: [{ name, columns, rows, serial? }] — or one sheet
 * given as { columns, rows, sheetName, serial? }. serial: a "Sr. No." column first.
 */
export function buildXlsx(input) {
    const sheets = input.sheets || [{ name: input.sheetName, columns: input.columns, rows: input.rows, serial: input.serial }];
    const names = sheetNames(sheets);
    const built = sheets.map((sheet) => worksheetXml(sheet.columns || [], sheet.rows || [], { serial: !!sheet.serial }));
    const quoted = (name) => `'${name.replace(/'/g, "''")}'`;

    const workbook = '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>'
        + '<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">'
        + `<sheets>${names.map((name, i) => `<sheet name="${xml(name)}" sheetId="${i + 1}" r:id="rId${i + 1}"/>`).join('')}</sheets>`
        + `<definedNames>${names.map((name, i) => `<definedName name="_xlnm._FilterDatabase" localSheetId="${i}" hidden="1">${xml(`${quoted(name)}!${built[i].filterRef}`)}</definedName>`).join('')}</definedNames>`
        + '</workbook>';
    const workbookRels = '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>'
        + '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">'
        + names.map((_, i) => `<Relationship Id="rId${i + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet${i + 1}.xml"/>`).join('')
        + `<Relationship Id="rId${names.length + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>`
        + '</Relationships>';
    const contentTypes = '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>'
        + '<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">'
        + '<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>'
        + '<Default Extension="xml" ContentType="application/xml"/>'
        + '<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>'
        + names.map((_, i) => `<Override PartName="/xl/worksheets/sheet${i + 1}.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>`).join('')
        + '<Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>'
        + '</Types>';
    const rootRels = '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>'
        + '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">'
        + '<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>'
        + '</Relationships>';

    return zipStored([
        { name: '[Content_Types].xml', data: utf8(contentTypes) },
        { name: '_rels/.rels', data: utf8(rootRels) },
        { name: 'xl/workbook.xml', data: utf8(workbook) },
        { name: 'xl/_rels/workbook.xml.rels', data: utf8(workbookRels) },
        { name: 'xl/styles.xml', data: utf8(STYLES_XML) },
        ...built.map((sheet, i) => ({ name: `xl/worksheets/sheet${i + 1}.xml`, data: utf8(sheet.xml) })),
    ]);
}

/**
 * Saves the workbook as `filename` (".xlsx" added if missing). Every sheet is
 * numbered (Sr. No.), as the tables on the screen are, unless it says serial: false.
 */
export function downloadXlsx(filename, input) {
    const numbered = (sheet) => ({ ...sheet, serial: sheet.serial !== false });
    const workbook = input.sheets
        ? { sheets: input.sheets.map(numbered) }
        : numbered(input);
    const blob = new Blob([buildXlsx(workbook)], { type: XLSX_MIME });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = /\.xlsx$/i.test(filename) ? filename : `${filename}.xlsx`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(a.href), 0);
}

/** `rows` as a one-sheet Excel file — the Excel twin of exportRowsToCsv. */
export function exportRowsToExcel(filename, columns, rows, { sheetName } = {}) {
    downloadXlsx(filename, { sheetName: sheetName || 'Report', columns, rows });
}

/**
 * Every row a paginated list has, page by page — for exporting more than the
 * page on screen. fetchPage({ page, limit }) resolves { rows, total }.
 * Stops at `max` rows (and says so with `capped`).
 */
export async function fetchAllRows(fetchPage, { pageSize = 500, max = 10000 } = {}) {
    const rows = [];
    let total = Infinity;
    for (let page = 1; rows.length < Math.min(total, max); page++) {
        const result = await fetchPage({ page, limit: pageSize });
        const pageRows = result?.rows || [];
        total = Number.isFinite(result?.total) ? result.total : rows.length + pageRows.length;
        rows.push(...pageRows);
        if (pageRows.length < pageSize) break;
    }
    return { rows: rows.slice(0, max), capped: total > max, total };
}

/**
 * A table the server lays out ({ header: [label…], rows: [[value…]…] }, an
 * export endpoint's format=json) saved as an Excel file — its columns stay
 * the server's, defined once.
 */
export function exportTableToExcel(filename, table, { sheetName } = {}) {
    const columns = (table?.header || []).map((label, i) => ({ label, value: (row) => row[i] }));
    downloadXlsx(filename, { sheetName: sheetName || 'Report', columns, rows: table?.rows || [] });
}
